import React, {
  type FocusEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  ButtonProps as AriaButtonProps,
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  OverlayArrow as AriaOverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from 'react-aria-components';
import { createPortal } from 'react-dom';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { cx } from '@/utils/cx';

interface TooltipProps
  extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, 'children'> {
  /**
   * The title of the tooltip.
   */
  title: ReactNode;
  /**
   * The description of the tooltip.
   */
  description?: ReactNode;
  /**
   * Whether to show the arrow on the tooltip.
   *
   * @default false
   */
  arrow?: boolean;
  /**
   * Delay in milliseconds before the tooltip is shown.
   *
   * @default 300
   */
  delay?: number;
  /**
   * Whether the tooltip content is interactive (hoverable).
   *
   * When true, the tooltip stays open while the mouse is over the trigger OR
   * the tooltip content. This allows users to interact with links, buttons, or
   * selectable text inside the tooltip.
   *
   * Implementation uses a portal to `document.body` with `position: fixed` so
   * the tooltip escapes ancestor `overflow: hidden` (e.g. inside a Modal) and
   * pointer enter/leave is tracked on both the trigger and the tooltip so
   * mouse movement between them is seamless.
   *
   * @default false
   */
  interactive?: boolean;
  /**
   * Override the wrapper element's className. Only applies in `interactive`
   * mode (where the Tooltip renders its own wrapper to attach pointer
   * listeners). Defaults to `'relative inline-flex'`, which suits inline
   * triggers like buttons. Pass `'relative block'` (or similar) when the
   * trigger needs to be a full-width block element — e.g. a menu item.
   */
  wrapperClassName?: string;
}

// =============================================================================
// InteractiveTooltip — portaled, self-positioned tooltip for hoverable content
// =============================================================================
//
// Renders the tooltip element via createPortal to document.body and positions
// it with `position: fixed` so it escapes any ancestor's `overflow: hidden`
// (e.g. inside a Modal whose wrapper clips its rounded corners). z-index
// comes from `useStackingZIndex` so the tooltip floats over the
// modal/slideout that anchors its trigger. Hover-stay behavior across the
// portal boundary works because pointer enter/leave handlers are attached to
// both the trigger wrapper and the portaled tooltip element — the same
// HIDE_DELAY hide-timer is shared between them.

const SHOW_DELAY = 300;
const HIDE_DELAY = 150;
/** Pixel gap between trigger and tooltip surface */
const TOOLTIP_GAP = 6;
/** Minimum pixel distance from viewport edge */
const VIEWPORT_PAD = 8;

function InteractiveTooltip({
  title,
  description,
  children,
  arrow = false,
  delay = SHOW_DELAY,
  isDisabled,
  placement = 'top',
  wrapperClassName = 'relative inline-flex',
}: Pick<
  TooltipProps,
  | 'title'
  | 'description'
  | 'children'
  | 'arrow'
  | 'delay'
  | 'isDisabled'
  | 'placement'
  | 'wrapperClassName'
>) {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // The tooltip is portaled to document.body, escaping any ancestor's
  // overflow:hidden. But the portal also escapes the natural DOM stacking
  // order. Read the current StackingContext (preserved across React portals)
  // and pin the tooltip 50 above the trigger's stacking layer so it renders
  // over the Modal/Slideout that anchors it without being so high that it
  // would cover a future nested overlay opened from inside the tooltip.
  const stackingZIndex = useStackingZIndex(50);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handlePointerEnter = useCallback(() => {
    if (isDisabled) {
      return;
    }
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      setVisible(true);
      showTimerRef.current = null;
    }, delay);
  }, [isDisabled, clearTimers, delay]);

  const handlePointerLeave = useCallback(() => {
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
      hideTimerRef.current = null;
    }, HIDE_DELAY);
  }, [clearTimers]);

  // The tooltip is rendered in a portal (see below), so it's no longer a DOM
  // descendant of the trigger wrapper. Pointer events on the tooltip don't
  // bubble up to the wrapper. To keep the tooltip open while hovered, we
  // attach the same enter/leave handlers to the tooltip element itself —
  // entering it cancels the hide timer; leaving it starts a new one. As long
  // as the user crosses between trigger and tooltip within HIDE_DELAY, the
  // visible state stays true.
  const handleTooltipPointerEnter = useCallback(() => {
    if (isDisabled) {
      return;
    }
    clearTimers();
  }, [isDisabled, clearTimers]);

  const handleTooltipPointerLeave = useCallback(() => {
    handlePointerLeave();
  }, [handlePointerLeave]);

  // Handle focus leaving the portaled tooltip content. If focus moves back
  // to the trigger wrapper, keep the tooltip open. Otherwise close it.
  const handleTooltipBlur = useCallback(
    (e: FocusEvent) => {
      const related = e.relatedTarget as Node | null;
      if (e.currentTarget.contains(related)) {
        return;
      }
      if (wrapperRef.current?.contains(related)) {
        return;
      }
      clearTimers();
      setVisible(false);
    },
    [clearTimers]
  );

  const handleFocus = useCallback(() => {
    if (isDisabled) {
      return;
    }
    clearTimers();
    setVisible(true);
  }, [isDisabled, clearTimers]);

  const handleBlur = useCallback(
    (e: FocusEvent) => {
      const related = e.relatedTarget as Node | null;
      // Check both the trigger wrapper (currentTarget) and the portaled
      // tooltip element — the tooltip is outside the DOM tree due to the
      // portal, so currentTarget.contains alone won't catch focus moving
      // into interactive tooltip content.
      if (e.currentTarget.contains(related)) {
        return;
      }
      if (tooltipRef.current?.contains(related)) {
        return;
      }
      clearTimers();
      setVisible(false);
    },
    [clearTimers]
  );

  // Measure the trigger and tooltip and position the tooltip with
  // `position: fixed` viewport coords, clamped to the viewport edges.
  const updatePosition = useCallback(() => {
    const el = tooltipRef.current;
    const trigger = wrapperRef.current;
    if (!el || !trigger) {
      return;
    }

    const gap = TOOLTIP_GAP;
    const pad = VIEWPORT_PAD;
    const triggerRect = trigger.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const parts = placement.split(' ');
    const base = parts[0] || 'top';
    const align = parts[1] as 'start' | 'end' | undefined;

    // --- Primary axis: which side of the trigger ---
    if (base === 'top' || base === 'bottom') {
      if (base === 'top') {
        el.style.top = `${triggerRect.top - elRect.height - gap}px`;
        el.style.bottom = 'auto';
      } else {
        el.style.top = `${triggerRect.bottom + gap}px`;
        el.style.bottom = 'auto';
      }

      // --- Cross axis: horizontal alignment, then clamp to viewport ---
      let targetLeft: number;
      if (align === 'start') {
        targetLeft = triggerRect.left;
      } else if (align === 'end') {
        targetLeft = triggerRect.right - elRect.width;
      } else {
        targetLeft =
          triggerRect.left + triggerRect.width / 2 - elRect.width / 2;
      }
      const maxLeft = window.innerWidth - elRect.width - pad;
      const clampedLeft = Math.max(pad, Math.min(targetLeft, maxLeft));
      el.style.left = `${clampedLeft}px`;
      el.style.right = 'auto';
      el.style.transform = 'none';
    } else {
      // left / right placement
      if (base === 'left') {
        el.style.left = `${triggerRect.left - elRect.width - gap}px`;
        el.style.right = 'auto';
      } else {
        el.style.left = `${triggerRect.right + gap}px`;
        el.style.right = 'auto';
      }

      // --- Cross axis: vertical alignment, then clamp to viewport ---
      let targetTop: number;
      if (align === 'start') {
        targetTop = triggerRect.top;
      } else if (align === 'end') {
        targetTop = triggerRect.bottom - elRect.height;
      } else {
        targetTop =
          triggerRect.top + triggerRect.height / 2 - elRect.height / 2;
      }
      const maxTop = window.innerHeight - elRect.height - pad;
      const clampedTop = Math.max(pad, Math.min(targetTop, maxTop));
      el.style.top = `${clampedTop}px`;
      el.style.bottom = 'auto';
      el.style.transform = 'none';
    }
  }, [placement]);

  // Position the tooltip on first render after it becomes visible.
  useLayoutEffect(() => {
    if (visible) {
      updatePosition();
    }
  }, [visible, updatePosition]);

  // Reposition on scroll/resize so the tooltip tracks its trigger.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleReposition = () => {
      updatePosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [visible, updatePosition]);

  // Inject aria-describedby onto the trigger child so screen readers
  // announce the tooltip content when the trigger is focused.
  // Merge with any existing aria-describedby to avoid overwriting it.
  const triggerChild = React.isValidElement<{ 'aria-describedby'?: string }>(
    children
  )
    ? React.cloneElement(children, {
        'aria-describedby': visible
          ? [children.props['aria-describedby'], tooltipId]
              .filter(Boolean)
              .join(' ') || undefined
          : children.props['aria-describedby'],
      })
    : children;

  // Render the tooltip element via a portal so it escapes ancestor
  // `overflow: hidden` clipping (e.g. inside a Modal). SSR-safe: portal only
  // renders on the client where `document` exists.
  const tooltipElement =
    visible && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            className={cx(
              // In dark mode `--color-bg-primary-solid` resolves to
              // `--color-bg-secondary`, so the bubble sits at almost the same
              // value as the page behind it and its edge disappears. A pale
              // hairline restores the separation. Matches the design system's
              // contrast-border strength (`--color-avatar-contrast-border`,
              // and `border-white/12` on solid buttons). The transparent
              // border in light mode keeps the box dimensions identical
              // across themes, so nothing shifts when the theme changes.
              'fixed flex w-max max-w-xs flex-col items-start gap-1 rounded-lg bg-primary-solid px-3 shadow-lg border border-transparent dark:border-white/15',
              description ? 'py-3' : 'py-2',
              'animate-in fade-in zoom-in-95 ease-out'
            )}
            style={{ zIndex: stackingZIndex }}
            role='tooltip'
            onPointerEnter={handleTooltipPointerEnter}
            onPointerLeave={handleTooltipPointerLeave}
            onBlur={handleTooltipBlur}
          >
            <span className='text-xs font-semibold text-white'>{title}</span>

            {description && (
              <span className='text-xs font-medium text-tooltip-supporting-text'>
                {description}
              </span>
            )}

            {arrow && (
              <AriaOverlayArrow>
                <svg
                  viewBox='0 0 100 100'
                  className='size-2.5 fill-bg-primary-solid in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180'
                >
                  <path d='M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z' />
                </svg>
              </AriaOverlayArrow>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={wrapperRef}
      className={wrapperClassName}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {triggerChild}
      {tooltipElement}
    </div>
  );
}

// =============================================================================
// Tooltip — standard react-aria tooltip (non-interactive)
// =============================================================================

export const Tooltip = ({
  title,
  description,
  children,
  arrow = false,
  delay = 300,
  closeDelay = 0,
  trigger,
  isDisabled,
  isOpen,
  defaultOpen,
  offset = 6,
  crossOffset,
  placement = 'top',
  onOpenChange,
  interactive = false,
  wrapperClassName,
  ...tooltipProps
}: TooltipProps) => {
  // Interactive tooltips use a separate non-portal implementation
  if (interactive) {
    return (
      <InteractiveTooltip
        title={title}
        description={description}
        arrow={arrow}
        delay={delay}
        isDisabled={isDisabled}
        placement={placement}
        wrapperClassName={wrapperClassName}
      >
        {children}
      </InteractiveTooltip>
    );
  }

  // Calculate cross offset based on placement alignment:
  // - "start" and "left" should shift tooltip right (positive offset)
  // - "end" and "right" should shift tooltip left (negative offset)
  const isStartAligned = [
    'top start',
    'top left',
    'bottom start',
    'bottom left',
  ].includes(placement);
  const isEndAligned = [
    'top end',
    'top right',
    'bottom end',
    'bottom right',
  ].includes(placement);
  let calculatedCrossOffset: number;
  if (isStartAligned) {
    calculatedCrossOffset = 12;
  } else if (isEndAligned) {
    calculatedCrossOffset = -12;
  } else {
    calculatedCrossOffset = 0;
  }

  return (
    <AriaTooltipTrigger
      {...{
        trigger,
        delay,
        closeDelay,
        isDisabled,
        isOpen,
        defaultOpen,
        onOpenChange,
      }}
    >
      {children}

      <AriaTooltip
        {...tooltipProps}
        offset={offset}
        placement={placement}
        crossOffset={crossOffset ?? calculatedCrossOffset}
        className={({ isEntering, isExiting }) =>
          cx(
            isEntering && 'ease-out animate-in',
            isExiting && 'ease-in animate-out'
          )
        }
      >
        {({ isEntering, isExiting }) => (
          <div
            className={cx(
              // Pale hairline in dark mode — see the InteractiveTooltip panel
              // above for why (solid bg collapses to bg-secondary in dark).
              'z-50 flex max-w-xs origin-(--trigger-anchor-point) flex-col items-start gap-1 rounded-lg bg-primary-solid px-3 shadow-lg will-change-transform border border-transparent dark:border-white/15',
              description ? 'py-3' : 'py-2',

              isEntering &&
                'ease-out animate-in fade-in zoom-in-95 in-placement-left:slide-in-from-right-0.5 in-placement-right:slide-in-from-left-0.5 in-placement-top:slide-in-from-bottom-0.5 in-placement-bottom:slide-in-from-top-0.5',
              isExiting &&
                'ease-in animate-out fade-out zoom-out-95 in-placement-left:slide-out-to-right-0.5 in-placement-right:slide-out-to-left-0.5 in-placement-top:slide-out-to-bottom-0.5 in-placement-bottom:slide-out-to-top-0.5'
            )}
          >
            <span className='text-xs font-semibold text-white'>{title}</span>

            {description && (
              <span className='text-xs font-medium text-tooltip-supporting-text'>
                {description}
              </span>
            )}

            {arrow && (
              <AriaOverlayArrow>
                <svg
                  viewBox='0 0 100 100'
                  className='size-2.5 fill-bg-primary-solid in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180'
                >
                  <path d='M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z' />
                </svg>
              </AriaOverlayArrow>
            )}
          </div>
        )}
      </AriaTooltip>
    </AriaTooltipTrigger>
  );
};

type TooltipTriggerProps = AriaButtonProps;

export const TooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  TooltipTriggerProps
>(function TooltipTrigger({ children, className, ...buttonProps }, ref) {
  return (
    <AriaButton
      ref={ref}
      {...buttonProps}
      className={values =>
        cx(
          'h-max w-max outline-hidden',
          typeof className === 'function' ? className(values) : className
        )
      }
    >
      {children}
    </AriaButton>
  );
});
